import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Palette, Shapes, Sparkles, Type, KeyRound, Plus, Edit, Trash2,
  Upload, Copy, RefreshCw, Eye, EyeOff, Power, FileImage, FileText as FileTextIcon,
  Flower2, Code2, Image as ImageIcon
} from 'lucide-react';

/**
 * EndpointReference — a small dev-aid panel shown at the bottom of every
 * "Edit asset" dialog in the Corel Integration admin. Lists the EXACT plugin
 * endpoints (URL + headers + curl) the CorelDRAW macro needs to fetch this
 * specific asset. Each line has a one-click copy button.
 *
 * `endpoints` is an array of `{ method, label, path }`. The full URL is
 * assembled from window.location.origin so users see the correct host
 * (preview vs production) and the values that come back from the macro will
 * actually work without editing.
 */
function EndpointReference({ assetId, companySlug, endpoints }) {
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied');
    } catch {
      toast.error('Could not copy — select the text manually');
    }
  };
  if (!assetId) return null;
  return (
    <div className="mt-4 pt-4 border-t border-slate-700/70" data-testid="endpoint-reference">
      <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wider text-slate-400">
        <Code2 className="w-3.5 h-3.5" />
        CorelDRAW macro endpoints (asset ID: <span className="font-mono normal-case text-slate-300">{assetId}</span>)
      </div>
      <div className="space-y-1.5">
        {endpoints.map((ep) => {
          const fullUrl = `${base}${ep.path}`;
          return (
            <div
              key={ep.path}
              className="flex items-center gap-2 bg-slate-950/60 border border-slate-800 rounded px-2 py-1.5 font-mono text-[11px] text-slate-200"
              data-testid={`endpoint-row-${ep.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <span className="text-fuchsia-400 shrink-0">{ep.method}</span>
              <span className="flex-1 break-all" title={fullUrl}>{fullUrl}</span>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-6 px-1.5 text-slate-400 hover:text-white hover:bg-slate-800"
                onClick={() => copy(fullUrl)}
                title={`Copy ${ep.label} URL`}
                data-testid={`copy-endpoint-${ep.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Copy className="w-3 h-3" />
              </Button>
            </div>
          );
        })}
      </div>
      <details className="mt-2 text-[11px] text-slate-400">
        <summary className="cursor-pointer hover:text-slate-200">
          Required headers + curl example
        </summary>
        <div className="mt-1.5 space-y-1 bg-slate-950/60 border border-slate-800 rounded px-2 py-1.5 font-mono text-[10px] text-slate-300">
          <div><span className="text-fuchsia-400">Authorization:</span> Bearer cdr_YOUR_API_KEY</div>
          <div><span className="text-fuchsia-400">X-Company-Slug:</span> {companySlug || '<your-company-slug>'}</div>
          <div className="mt-2 pt-2 border-t border-slate-800 whitespace-pre-wrap break-all">
            {`curl -H "Authorization: Bearer cdr_YOUR_API_KEY" -H "X-Company-Slug: ${companySlug || '<slug>'}" "${base}${endpoints[0]?.path || ''}"`}
          </div>
        </div>
      </details>
    </div>
  );
}

const TABS = [
  { id: 'granites', label: 'Granite Library', icon: Palette },
  { id: 'vases', label: 'Vase Inserts', icon: Flower2 },
  { id: 'shapes', label: 'Memorial Shapes', icon: Shapes },
  { id: 'motifs', label: 'Motifs', icon: Sparkles },
  { id: 'misc', label: 'Misc', icon: ImageIcon },
  { id: 'text', label: 'Text Settings', icon: Type },
  { id: 'keys', label: 'API Keys', icon: KeyRound }
];

// <img> can't send Authorization headers — fetch the binary with axios and render as blob URL.
function AuthImage({ url, token, alt, className, fallback }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    setErr(false);
    setSrc(null);
    if (!url) return;
    axios.get(url, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' })
      .then(res => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(res.data);
        setSrc(createdUrl);
      })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => { cancelled = true; if (createdUrl) URL.revokeObjectURL(createdUrl); };
  }, [url, token]);
  if (err || !src) return fallback || null;
  return <img src={src} alt={alt} className={className} />;
}

// File-picker button that owns its own input. Avoids the label/Button double-trigger bug.
function FilePickerButton({ accept, onPick, children, className, testId }) {
  const ref = React.useRef(null);
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept={accept}
        className="hidden"
        data-testid={testId}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          // Reset AFTER calling onPick so the File reference stays valid in all browsers.
          // Also allows the same file name to be picked again next time.
          setTimeout(() => { try { if (ref.current) ref.current.value = ''; } catch (_) {} }, 0);
        }}
      />
      <Button type="button" size="sm" variant="outline" className={className} onClick={(e) => { e.stopPropagation(); ref.current?.click(); }}>
        {children}
      </Button>
    </>
  );
}

export default function CorelIntegration() {
  const navigate = useNavigate();
  const { user, token, API } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('granites');

  if (!user || !['admin', 'super_admin', 'company_admin'].includes(user.role)) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Access denied</h2>
          <p className="text-slate-400">Only admins can access Corel Integration.</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4" data-testid="back-to-dashboard-btn">Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="text-slate-300 hover:text-white hover:bg-white/10" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-fuchsia-400 to-purple-400 bg-clip-text text-transparent">COREL INTEGRATION</h1>
              <p className="text-sm text-slate-400 mt-1">Master cloud library — granites, shapes, motifs, settings and API keys. Memorial Desk is the source of truth; design clients are connected over the network.</p>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-7 bg-slate-800/60 border border-slate-700 p-1 rounded-lg mb-6">
            {TABS.map(t => (
              <TabsTrigger key={t.id} value={t.id} className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white text-slate-300" data-testid={`corel-tab-${t.id}`}>
                <t.icon className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">{t.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="granites"><GraniteLibrary API={API} token={token} companySlug={user.company_id} /></TabsContent>
          <TabsContent value="vases"><VaseInsertLibrary API={API} token={token} companySlug={user.company_id} /></TabsContent>
          <TabsContent value="shapes"><ShapeLibrary API={API} token={token} companySlug={user.company_id} /></TabsContent>
          <TabsContent value="motifs"><MotifLibrary API={API} token={token} companySlug={user.company_id} /></TabsContent>
          <TabsContent value="misc"><MiscLibrary API={API} token={token} companySlug={user.company_id} /></TabsContent>
          <TabsContent value="text"><TextSettings API={API} token={token} /></TabsContent>
          <TabsContent value="keys"><ApiKeys API={API} token={token} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ============================ GRANITE LIBRARY ============================
function GraniteLibrary({ API, token, companySlug }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', supplier: '', category: 'Granite', colour_family: '', finish: 'Polished', active: true, notes: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/corel/admin/granites`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(r.data || []);
    } catch (e) { toast.error('Failed to load granites'); }
    finally { setLoading(false); }
  }, [API, token]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm({ name: '', supplier: '', category: 'Granite', colour_family: '', finish: 'Polished', active: true, notes: '' }); setDialogOpen(true); };
  const openEdit = (g) => { setEditing(g); setForm({ name: g.name || '', supplier: g.supplier || '', category: g.category || 'Granite', colour_family: g.colour_family || '', finish: g.finish || 'Polished', active: !!g.active, notes: g.notes || '' }); setDialogOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    try {
      if (editing) {
        await axios.put(`${API}/corel/admin/granites/${editing.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Granite updated');
      } else {
        await axios.post(`${API}/corel/admin/granites`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Granite created');
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error('Save failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this granite?')) return;
    try {
      await axios.delete(`${API}/corel/admin/granites/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted');
      load();
    } catch (e) { toast.error('Delete failed'); }
  };

  const uploadTexture = async (id, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      await axios.post(`${API}/corel/admin/granites/${id}/upload-texture`, fd, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Texture uploaded');
      load();
    } catch (e) { toast.error('Upload failed'); }
  };

  const uploadTextureKind = async (id, kind, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      await axios.post(`${API}/corel/admin/granites/${id}/upload-${kind}-texture`, fd, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${kind} texture uploaded`);
      load();
    } catch (e) {
      console.error(`Upload ${kind} texture failed:`, e?.response?.status, e?.response?.data, e?.message);
      const detail = e?.response?.data?.detail;
      let msg;
      if (typeof detail === 'string') msg = detail;
      else if (detail?.message) msg = detail.message;
      else if (detail?.error) msg = detail.error;
      else if (e?.response?.status) msg = `Upload failed (${e.response.status})`;
      else msg = `Upload failed: ${e?.message || 'network error'}`;
      toast.error(msg);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Granite Library</h2>
          <p className="text-sm text-slate-400">Texture images & metadata. Plugin pulls these as fills.</p>
        </div>
        <Button onClick={openCreate} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="add-granite-btn"><Plus className="w-4 h-4 mr-2" /> Add Granite</Button>
      </div>
      {loading ? <div className="text-slate-400">Loading…</div> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="granite-grid">
          {items.length === 0 && <div className="col-span-full text-slate-500 italic text-center py-12">No granites yet — add your first one.</div>}
          {items.map(g => (
            <Card key={g.id} className="bg-slate-800/60 border-slate-700 overflow-hidden" data-testid={`granite-${g.id}`}>
              <div className="aspect-square bg-slate-900 flex items-center justify-center relative">
                {g.has_thumbnail ? (
                  <AuthImage url={`${API}/corel/admin/granites/${g.id}/thumbnail`} token={token} alt={g.name} className="w-full h-full object-cover" fallback={<FileImage className="w-12 h-12 text-slate-600" />} />
                ) : (
                  <FileImage className="w-12 h-12 text-slate-600" />
                )}
                {!g.active && <span className="absolute top-2 right-2 px-2 py-0.5 bg-slate-900/80 text-xs rounded">Inactive</span>}
                {g.is_default && <span className="absolute top-2 left-2 px-2 py-0.5 bg-fuchsia-900/80 text-fuchsia-200 text-xs rounded border border-fuchsia-700" data-testid={`default-badge-granite-${g.id}`}>Default</span>}
              </div>
              <CardContent className="p-3 space-y-1">
                <div className="font-semibold text-white truncate">{g.name}</div>
                <div className="text-xs text-slate-400">{g.supplier || '—'} · {g.colour_family || '—'}</div>
                <div className="text-xs text-slate-500">{g.finish} · {g.category}</div>
                <div className="flex items-center gap-2 pt-1 text-[10px]">
                  <span className={`px-1.5 py-0.5 rounded ${g.has_light_texture ? 'bg-emerald-900/40 text-emerald-300 border border-emerald-700' : 'bg-slate-700 text-slate-400 border border-slate-600'}`}>Light {g.has_light_texture ? '✓' : '—'}</span>
                  <span className={`px-1.5 py-0.5 rounded ${g.has_dark_texture ? 'bg-emerald-900/40 text-emerald-300 border border-emerald-700' : 'bg-slate-700 text-slate-400 border border-slate-600'}`}>Dark {g.has_dark_texture ? '✓' : '—'}</span>
                </div>
                {g.is_default ? (
                  <div className="pt-2 text-[10px] text-slate-500 italic">Shared default library — read only</div>
                ) : (
                <div className="flex flex-wrap gap-1 pt-2">
                  <FilePickerButton
                    accept="image/png,image/jpeg,.png,.jpg,.jpeg"
                    onPick={(f) => uploadTextureKind(g.id, 'light', f)}
                    className="border-slate-600 text-slate-200 hover:bg-slate-700"
                    testId={`upload-light-texture-${g.id}`}
                  >
                    <Upload className="w-3 h-3 mr-1" /> Light
                  </FilePickerButton>
                  <FilePickerButton
                    accept="image/png,image/jpeg,.png,.jpg,.jpeg"
                    onPick={(f) => uploadTextureKind(g.id, 'dark', f)}
                    className="border-slate-600 text-slate-200 hover:bg-slate-700"
                    testId={`upload-dark-texture-${g.id}`}
                  >
                    <Upload className="w-3 h-3 mr-1" /> Dark
                  </FilePickerButton>
                  <Button size="sm" variant="outline" className="border-slate-600 text-slate-200 hover:bg-slate-700" onClick={() => openEdit(g)} data-testid={`edit-granite-${g.id}`}><Edit className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" className="border-red-700 text-red-400 hover:bg-red-900/30" onClick={() => del(g.id)} data-testid={`delete-granite-${g.id}`}><Trash2 className="w-3 h-3" /></Button>
                </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Granite' : 'Add Granite'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-slate-200">Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-slate-800 border-slate-600 text-slate-100" data-testid="granite-name-input" /></div>
            <div><Label className="text-slate-200">Supplier</Label><Input value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} className="bg-slate-800 border-slate-600 text-slate-100" /></div>
            <div><Label className="text-slate-200">Category</Label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded">
                {['Granite', 'Softer Stone'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div><Label className="text-slate-200">Colour Family</Label><Input value={form.colour_family} onChange={e => setForm({ ...form, colour_family: e.target.value })} className="bg-slate-800 border-slate-600 text-slate-100" /></div>
            <div><Label className="text-slate-200">Finish</Label>
              <select value={form.finish} onChange={e => setForm({ ...form, finish: e.target.value })} className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded">
                {['Polished', 'Honed', 'Flamed', 'Brushed', 'Sandblasted', 'Other'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="col-span-2"><Label className="text-slate-200">Notes</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="bg-slate-800 border-slate-600 text-slate-100" /></div>
            <div className="col-span-2 flex items-center gap-2"><Switch checked={form.active} onCheckedChange={v => setForm({ ...form, active: v })} /><Label className="text-slate-200">Active</Label></div>
          </div>
          {editing && (
            <EndpointReference
              assetId={editing.id}
              companySlug={companySlug}
              endpoints={[
                { method: 'GET', label: 'list', path: '/api/corel/plugin/granites' },
                { method: 'GET', label: 'light texture', path: `/api/corel/plugin/granites/${editing.id}/light-texture` },
                { method: 'GET', label: 'dark texture', path: `/api/corel/plugin/granites/${editing.id}/dark-texture` },
                { method: 'GET', label: 'thumbnail', path: `/api/corel/plugin/granites/${editing.id}/thumbnail` },
              ]}
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-600">Cancel</Button>
            <Button onClick={save} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="save-granite-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================ VASE INSERT LIBRARY ============================
const VASE_POSITION_LABELS = {
  left: 'Left',
  right: 'Right',
  centre: 'Centre',
  left_right: 'Left & Right',
};
const VASE_POSITIONS = ['left', 'right', 'centre', 'left_right'];

function VaseInsertLibrary({ API, token, companySlug }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterPos, setFilterPos] = useState('all');
  const [form, setForm] = useState({ name: '', position: 'centre', supplier: '', notes: '', active: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/corel/admin/vase-inserts`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(r.data || []);
    } catch (e) { toast.error('Failed to load vase inserts'); }
    finally { setLoading(false); }
  }, [API, token]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm({ name: '', position: 'centre', supplier: '', notes: '', active: true }); setDialogOpen(true); };
  const openEdit = (v) => { setEditing(v); setForm({ name: v.name || '', position: v.position || 'centre', supplier: v.supplier || '', notes: v.notes || '', active: !!v.active }); setDialogOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    try {
      if (editing) {
        await axios.put(`${API}/corel/admin/vase-inserts/${editing.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Vase insert updated');
      } else {
        await axios.post(`${API}/corel/admin/vase-inserts`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Vase insert created');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Save failed');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this vase insert (both silver and gold images)?')) return;
    try {
      await axios.delete(`${API}/corel/admin/vase-inserts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted');
      load();
    } catch (e) { toast.error('Delete failed'); }
  };

  const uploadImage = async (id, kind, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      await axios.post(`${API}/corel/admin/vase-inserts/${id}/upload-${kind}`, fd, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${kind === 'silver' ? 'Silver' : 'Gold'} image uploaded`);
      load();
    } catch (e) {
      console.error(`Upload ${kind} failed:`, e?.response?.status, e?.response?.data, e?.message);
      const detail = e?.response?.data?.detail;
      let msg;
      if (typeof detail === 'string') msg = detail;
      else if (e?.response?.status) msg = `Upload failed (${e.response.status})`;
      else msg = `Upload failed: ${e?.message || 'network error'}`;
      toast.error(msg);
    }
  };

  const filtered = filterPos === 'all' ? items : items.filter(v => v.position === filterPos);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold">Vase Inserts</h2>
          <p className="text-sm text-slate-400">Position variants (left / right / centre / left &amp; right) with silver &amp; gold colour finishes.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filterPos}
            onChange={e => setFilterPos(e.target.value)}
            className="px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm"
            data-testid="vase-position-filter"
          >
            <option value="all">All positions</option>
            {VASE_POSITIONS.map(p => <option key={p} value={p}>{VASE_POSITION_LABELS[p]}</option>)}
          </select>
          <Button onClick={openCreate} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="add-vase-insert-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Vase Insert
          </Button>
        </div>
      </div>

      {loading ? <div className="text-slate-400">Loading…</div> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="vase-insert-grid">
          {filtered.length === 0 && (
            <div className="col-span-full text-slate-500 italic text-center py-12">
              No vase inserts {filterPos !== 'all' ? `for ${VASE_POSITION_LABELS[filterPos]}` : 'yet'} — add your first one.
            </div>
          )}
          {filtered.map(v => (
            <Card key={v.id} className="bg-slate-800/60 border-slate-700 overflow-hidden" data-testid={`vase-insert-${v.id}`}>
              <div className="grid grid-cols-2 bg-slate-900">
                <div className="aspect-square flex items-center justify-center border-r border-slate-800 bg-slate-950/50">
                  {v.has_silver ? (
                    <AuthImage url={`${API}/corel/admin/vase-inserts/${v.id}/silver`} token={token} alt={`${v.name} silver`} className="w-full h-full object-contain" fallback={<FileImage className="w-10 h-10 text-slate-600" />} />
                  ) : (
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">Silver —</div>
                  )}
                </div>
                <div className="aspect-square flex items-center justify-center bg-amber-950/20">
                  {v.has_gold ? (
                    <AuthImage url={`${API}/corel/admin/vase-inserts/${v.id}/gold`} token={token} alt={`${v.name} gold`} className="w-full h-full object-contain" fallback={<FileImage className="w-10 h-10 text-slate-600" />} />
                  ) : (
                    <div className="text-[10px] text-amber-700/70 uppercase tracking-wider">Gold —</div>
                  )}
                </div>
              </div>
              <CardContent className="p-3 space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-semibold text-white truncate">{v.name}</div>
                  <div className="flex gap-1">
                    {v.is_default && <span className="text-[10px] px-1.5 py-0.5 bg-fuchsia-900/80 text-fuchsia-200 rounded border border-fuchsia-700" data-testid={`default-badge-vase-${v.id}`}>Default</span>}
                    {!v.active && <span className="text-[10px] px-1.5 py-0.5 bg-slate-700 text-slate-300 rounded">Inactive</span>}
                  </div>
                </div>
                <div className="text-xs text-slate-400">{v.supplier || '—'}</div>
                <Badge className="bg-purple-900/50 text-purple-200 border-purple-700 text-[10px]">{VASE_POSITION_LABELS[v.position] || v.position}</Badge>
                <div className="flex items-center gap-2 pt-1 text-[10px]">
                  <span className={`px-1.5 py-0.5 rounded ${v.has_silver ? 'bg-slate-700 text-slate-200 border border-slate-500' : 'bg-slate-800 text-slate-500 border border-slate-700'}`}>Silver {v.has_silver ? '✓' : '—'}</span>
                  <span className={`px-1.5 py-0.5 rounded ${v.has_gold ? 'bg-amber-900/40 text-amber-200 border border-amber-700' : 'bg-slate-800 text-slate-500 border border-slate-700'}`}>Gold {v.has_gold ? '✓' : '—'}</span>
                </div>
                {v.is_default ? (
                  <div className="pt-2 text-[10px] text-slate-500 italic">Shared default library — read only</div>
                ) : (
                <div className="flex flex-wrap gap-1 pt-2">
                  <FilePickerButton
                    accept="image/png,image/jpeg,.png,.jpg,.jpeg"
                    onPick={(f) => uploadImage(v.id, 'silver', f)}
                    className="border-slate-500 text-slate-200 hover:bg-slate-700"
                    testId={`upload-silver-${v.id}`}
                  >
                    <Upload className="w-3 h-3 mr-1" /> Silver
                  </FilePickerButton>
                  <FilePickerButton
                    accept="image/png,image/jpeg,.png,.jpg,.jpeg"
                    onPick={(f) => uploadImage(v.id, 'gold', f)}
                    className="border-amber-700 text-amber-200 hover:bg-amber-900/30"
                    testId={`upload-gold-${v.id}`}
                  >
                    <Upload className="w-3 h-3 mr-1" /> Gold
                  </FilePickerButton>
                  <Button size="sm" variant="outline" className="border-slate-600 text-slate-200 hover:bg-slate-700" onClick={() => openEdit(v)} data-testid={`edit-vase-${v.id}`}><Edit className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" className="border-red-700 text-red-400 hover:bg-red-900/30" onClick={() => del(v.id)} data-testid={`delete-vase-${v.id}`}><Trash2 className="w-3 h-3" /></Button>
                </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Vase Insert' : 'Add Vase Insert'}</DialogTitle>
            <DialogDescription className="text-slate-400">After saving, upload the silver and gold images using the buttons on the card.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-slate-200">Name</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-slate-800 border-slate-600 text-slate-100" data-testid="vase-name-input" />
            </div>
            <div>
              <Label className="text-slate-200">Position</Label>
              <select
                value={form.position}
                onChange={e => setForm({ ...form, position: e.target.value })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100"
                data-testid="vase-position-select"
              >
                {VASE_POSITIONS.map(p => <option key={p} value={p}>{VASE_POSITION_LABELS[p]}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-slate-200">Supplier</Label>
              <Input value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} className="bg-slate-800 border-slate-600 text-slate-100" />
            </div>
            <div className="col-span-2">
              <Label className="text-slate-200">Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="bg-slate-800 border-slate-600 text-slate-100" />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={v => setForm({ ...form, active: v })} />
              <Label className="text-slate-200">Active</Label>
            </div>
          </div>
          {editing && (
            <EndpointReference
              assetId={editing.id}
              companySlug={companySlug}
              endpoints={[
                { method: 'GET', label: 'list', path: '/api/corel/plugin/vase-inserts' },
                { method: 'GET', label: 'silver image', path: `/api/corel/plugin/vase-inserts/${editing.id}/silver` },
                { method: 'GET', label: 'gold image', path: `/api/corel/plugin/vase-inserts/${editing.id}/gold` },
              ]}
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-600">Cancel</Button>
            <Button onClick={save} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="save-vase-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================ SHAPE LIBRARY ============================
function ShapeLibrary({ API, token, companySlug }) {
  return <AssetLibrary
    title="Memorial Shapes"
    subtitle="SVG / CDR / AI templates. Plugin auto-creates memorials from these."
    apiBase={`${API}/corel/admin/shapes`}
    pluginPath="/api/corel/plugin/shapes"
    token={token}
    companySlug={companySlug}
    extraFields={[
      { key: 'memorial_type', label: 'Memorial Type', type: 'select', options: ['Cremation Memorial', 'Full Size Memorial', 'Headstone', 'Kerb Set', 'Vase', 'Plaque', 'Other'] },
      { key: 'category', label: 'Category', type: 'text' },
      { key: 'width_mm', label: 'Width (mm)', type: 'number' },
      { key: 'height_mm', label: 'Height (mm)', type: 'number' }
    ]}
    vectorKind="vector"
    vectorAccept=".svg,.cdr,.ai,.pdf,.eps"
  />;
}

// ============================ MISC: Background image ============================
// Singleton-per-company background PNG. Used by the Corel plugin via
// /api/corel/plugin/misc/background. Upload replaces the previous image
// atomically; Delete clears it.
function MiscLibrary({ API, token, companySlug }) {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  // Cache-busting key so the preview <img> reloads after upload/replace,
  // and to force refetch of the metadata after upload/delete.
  const [previewKey, setPreviewKey] = useState(Date.now());

  // Fetch background metadata — re-runs whenever previewKey bumps.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/corel/admin/misc/background`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (cancelled) return;
        setMeta(r.data || { has_background: false });
      } catch (e) {
        if (cancelled) return;
        setMeta({ has_background: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [API, token, previewKey]);

  const upload = async (file) => {
    if (!file) return;
    const okPng = /\.png$/i.test(file.name) || file.type === 'image/png';
    const okJpg = /\.(jpg|jpeg)$/i.test(file.name) || file.type === 'image/jpeg' || file.type === 'image/jpg';
    if (!okPng && !okJpg) {
      toast.error('Background must be a PNG or JPG file.');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/corel/admin/misc/background`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Background image uploaded.');
      setPreviewKey(Date.now());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    }
  };

  const remove = async () => {
    if (!window.confirm('Remove the current background image?')) return;
    try {
      await axios.delete(`${API}/corel/admin/misc/background`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Background removed.');
      setPreviewKey(Date.now());
    } catch (e) {
      toast.error('Failed to remove background');
    }
  };

  const fmtBytes = (n) => {
    if (!n && n !== 0) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold">Misc — Background Image</h2>
          <p className="text-sm text-slate-400 mt-1">
            One background image per company (PNG or JPG). The Corel plugin can fetch it via{' '}
            <code className="px-1 py-0.5 bg-slate-800 rounded text-fuchsia-300">/api/corel/plugin/misc/background</code>{' '}
            to use as a memorial layout background.
          </p>
        </div>
        <FilePickerButton
          accept=".png,.jpg,.jpeg,image/png,image/jpeg"
          onPick={upload}
          className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white border-fuchsia-600"
          testId="misc-background-upload-input"
        >
          <Upload className="w-4 h-4 mr-2" />
          {meta?.has_background ? 'Replace Background' : 'Upload Background'}
        </FilePickerButton>
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm">Loading…</div>
      ) : meta?.has_background ? (
        <div className="rounded-lg border border-slate-700 bg-slate-900/60 p-4 space-y-3" data-testid="misc-background-card">
          <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-4 items-start">
            <div className="rounded border border-slate-700 bg-[#1a1a1a] p-2 flex items-center justify-center min-h-[180px]">
              {/* Background preview — checkerboard-ish dark surface so any
                  transparency reads correctly. Cache-busted via previewKey. */}
              <AuthImage
                url={`${API}/corel/admin/misc/background/image?v=${previewKey}`}
                token={token}
                alt="Background"
                className="max-w-full max-h-[260px] object-contain"
                fallback={<span className="text-slate-500 text-xs">Preview unavailable</span>}
              />
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex gap-2 items-center"><FileImage className="w-4 h-4 text-slate-400" /> <span className="text-slate-200">{meta.filename || 'background.png'}</span></div>
              <div className="text-slate-400">Type: <span className="text-slate-200">{meta.content_type || 'image/png'}</span></div>
              <div className="text-slate-400">Size: <span className="text-slate-200">{fmtBytes(meta.size)}</span></div>
              {meta.updated_at && (
                <div className="text-slate-400">Updated: <span className="text-slate-200">{new Date(meta.updated_at).toLocaleString()}</span></div>
              )}
              <div className="pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={remove}
                  data-testid="misc-background-delete-btn"
                  className="border-red-700 text-red-400 hover:bg-red-900/30"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Remove Background
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border-2 border-dashed border-slate-700 p-10 text-center text-slate-400" data-testid="misc-background-empty">
          <ImageIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No background uploaded yet.</p>
          <p className="text-xs mt-1">Click &ldquo;Upload Background&rdquo; above to add a PNG or JPG image.</p>
        </div>
      )}

      <EndpointReference
        assetId="background"
        companySlug={companySlug}
        endpoints={[
          { method: 'GET', label: 'background (binary)', path: '/api/corel/plugin/misc/background' },
          { method: 'GET', label: 'background info', path: '/api/corel/plugin/misc/background/info' }
        ]}
      />
    </div>
  );
}

// ============================ MOTIF LIBRARY ============================
function MotifLibrary({ API, token, companySlug }) {
  return <AssetLibrary
    title="Motifs"
    subtitle="Decorative vectors (crosses, doves, flowers, sports etc.) the plugin can drop into designs."
    apiBase={`${API}/corel/admin/motifs`}
    pluginPath="/api/corel/plugin/motifs"
    token={token}
    companySlug={companySlug}
    extraFields={[
      { key: 'category', label: 'Category', type: 'select', options: ['Religious', 'Floral', 'Nature', 'Sports', 'Hobbies', 'Family', 'Military', 'Other'] }
    ]}
    vectorKind="vector"
    vectorAccept=".svg,.cdr,.ai,.pdf,.eps"
    searchable
  />;
}

// Generic asset list+grid component for shapes & motifs (granite has bespoke UI for swatches)
function AssetLibrary({ title, subtitle, apiBase, pluginPath, token, companySlug, extraFields, vectorKind, vectorAccept, searchable }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const initial = { name: '', active: true, notes: '' };
  extraFields.forEach(f => initial[f.key] = f.type === 'select' ? (f.options[0] || '') : '');
  const [form, setForm] = useState(initial);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(apiBase, { headers: { Authorization: `Bearer ${token}` }, params: searchable && search ? { q: search } : {} });
      setItems(r.data || []);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [apiBase, token, search, searchable]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm({ ...initial }); setDialogOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    const f = { name: it.name || '', active: !!it.active, notes: it.notes || '' };
    extraFields.forEach(fd => f[fd.key] = it[fd.key] ?? (fd.type === 'select' ? fd.options[0] : ''));
    setForm(f);
    setDialogOpen(true);
  };
  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    try {
      const payload = { ...form };
      extraFields.forEach(f => {
        if (f.type === 'number' && payload[f.key] !== '' && payload[f.key] !== null && payload[f.key] !== undefined) payload[f.key] = parseFloat(payload[f.key]);
        if (payload[f.key] === '') payload[f.key] = null;
      });
      if (editing) await axios.put(`${apiBase}/${editing.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      else await axios.post(apiBase, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Saved');
      setDialogOpen(false);
      load();
    } catch (e) { toast.error('Save failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    await axios.delete(`${apiBase}/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    toast.success('Deleted'); load();
  };
  const uploadFile = async (id, kind, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      await axios.post(`${apiBase}/${id}/upload-${kind}`, fd, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${kind} uploaded`); load();
    } catch (e) { toast.error('Upload failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-semibold">{title}</h2>
          <p className="text-sm text-slate-400">{subtitle}</p>
        </div>
        <div className="flex gap-2">
          {searchable && (
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" className="bg-slate-800 border-slate-600 text-slate-100 placeholder:text-slate-500 w-48" />
          )}
          <Button onClick={openCreate} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid={`add-${vectorKind}-btn`}><Plus className="w-4 h-4 mr-2" /> Add</Button>
        </div>
      </div>
      {loading ? <div className="text-slate-400">Loading…</div> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.length === 0 && <div className="col-span-full text-slate-500 italic text-center py-12">Nothing here yet.</div>}
          {items.map(it => (
            <Card key={it.id} className="bg-slate-800/60 border-slate-700 overflow-hidden">
              <div className="aspect-square bg-slate-900 flex items-center justify-center relative">
                {it.has_thumbnail ? (
                  <AuthImage url={`${apiBase}/${it.id}/thumbnail`} token={token} alt={it.name} className="w-full h-full object-contain p-3" fallback={<FileTextIcon className="w-12 h-12 text-slate-600" />} />
                ) : (
                  <FileTextIcon className="w-12 h-12 text-slate-600" />
                )}
                {it.has_vector && <Badge className="absolute top-2 left-2 bg-emerald-700 text-white text-xs">VECTOR</Badge>}
                {it.is_default && <Badge className="absolute bottom-2 left-2 bg-fuchsia-900/80 text-fuchsia-200 border border-fuchsia-700 text-xs" data-testid={`default-badge-${it.id}`}>Default</Badge>}
                {!it.active && <span className="absolute top-2 right-2 px-2 py-0.5 bg-slate-900/80 text-xs rounded">Inactive</span>}
              </div>
              <CardContent className="p-3 space-y-1">
                <div className="font-semibold text-white truncate">{it.name}</div>
                <div className="text-xs text-slate-400 truncate">
                  {extraFields.map(f => it[f.key]).filter(Boolean).join(' · ') || '—'}
                </div>
                {it.is_default ? (
                  <div className="pt-2 text-[10px] text-slate-500 italic">Shared default library — read only</div>
                ) : (
                <div className="flex flex-wrap gap-1 pt-2">
                  <FilePickerButton accept={vectorAccept} onPick={(f) => uploadFile(it.id, 'vector', f)} className="border-slate-600 text-slate-200 hover:bg-slate-700">
                    <Upload className="w-3 h-3 mr-1" />Vec
                  </FilePickerButton>
                  <FilePickerButton accept="image/png,image/jpeg,.png,.jpg,.jpeg" onPick={(f) => uploadFile(it.id, 'thumbnail', f)} className="border-slate-600 text-slate-200 hover:bg-slate-700">
                    <Upload className="w-3 h-3 mr-1" />Thumb
                  </FilePickerButton>
                  <Button size="sm" variant="outline" className="border-slate-600 text-slate-200 hover:bg-slate-700" onClick={() => openEdit(it)}><Edit className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" className="border-red-700 text-red-400 hover:bg-red-900/30" onClick={() => del(it.id)}><Trash2 className="w-3 h-3" /></Button>
                </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? `Edit ${title}` : `Add to ${title}`}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-slate-200">Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-slate-800 border-slate-600 text-slate-100" /></div>
            {extraFields.map(f => (
              <div key={f.key} className={f.type === 'select' || f.key === 'category' ? 'col-span-2' : 'col-span-1'}>
                <Label className="text-slate-200">{f.label}</Label>
                {f.type === 'select' ? (
                  <select value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded">
                    {f.options.map(o => <option key={o}>{o}</option>)}
                  </select>
                ) : (
                  <Input type={f.type === 'number' ? 'number' : 'text'} value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} className="bg-slate-800 border-slate-600 text-slate-100" />
                )}
              </div>
            ))}
            <div className="col-span-2"><Label className="text-slate-200">Notes</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="bg-slate-800 border-slate-600 text-slate-100" /></div>
            <div className="col-span-2 flex items-center gap-2"><Switch checked={form.active} onCheckedChange={v => setForm({ ...form, active: v })} /><Label className="text-slate-200">Active</Label></div>
          </div>
          {editing && pluginPath && (
            <EndpointReference
              assetId={editing.id}
              companySlug={companySlug}
              endpoints={[
                { method: 'GET', label: 'list', path: pluginPath },
                { method: 'GET', label: 'vector', path: `${pluginPath}/${editing.id}/${vectorKind || 'vector'}` },
                { method: 'GET', label: 'thumbnail', path: `${pluginPath}/${editing.id}/thumbnail` },
              ]}
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-600">Cancel</Button>
            <Button onClick={save} className="bg-fuchsia-600 hover:bg-fuchsia-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================ TEXT SETTINGS ============================
function TextSettings({ API, token }) {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/corel/admin/text-settings`, { headers: { Authorization: `Bearer ${token}` } });
      setS(r.data);
    } catch (e) { toast.error('Failed to load text settings'); }
    finally { setLoading(false); }
  }, [API, token]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/corel/admin/text-settings`, s, { headers: { Authorization: `Bearer ${token}` } });
      setS(r.data); toast.success('Settings saved');
    } catch (e) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  if (loading || !s) return <div className="text-slate-400">Loading…</div>;

  return (
    <Card className="bg-slate-800/60 border-slate-700 text-slate-100">
      <CardHeader>
        <CardTitle className="text-xl text-white">Text Tool Defaults</CardTitle>
        <CardDescription className="text-slate-400">Used by the design plugin when laying out inscriptions.</CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><Label className="text-slate-200">Default Line Spacing (%)</Label><Input type="number" step="1" value={s.default_line_spacing_pct ?? 100} onChange={e => setS({ ...s, default_line_spacing_pct: parseFloat(e.target.value) })} className="bg-slate-900 border-slate-600 text-slate-100" /></div>
        <div><Label className="text-slate-200">Minimum Font Size (pt)</Label><Input type="number" step="0.5" value={s.minimum_font_size_pt ?? 10} onChange={e => setS({ ...s, minimum_font_size_pt: parseFloat(e.target.value) })} className="bg-slate-900 border-slate-600 text-slate-100" /></div>
        <div className="md:col-span-2"><Label className="text-slate-200">Default Inscription Font</Label><Input value={s.default_inscription_font || ''} onChange={e => setS({ ...s, default_inscription_font: e.target.value })} className="bg-slate-900 border-slate-600 text-slate-100 placeholder:text-slate-500" placeholder="Times New Roman" /></div>
        <div><Label className="text-slate-200">Date Suffix Scale (%)</Label><Input type="number" step="1" value={s.date_suffix_scale_pct ?? 60} onChange={e => setS({ ...s, date_suffix_scale_pct: parseFloat(e.target.value) })} className="bg-slate-900 border-slate-600 text-slate-100" /></div>
        <div><Label className="text-slate-200">Date Suffix Baseline Shift (%)</Label><Input type="number" step="1" value={s.date_suffix_baseline_shift_pct ?? 30} onChange={e => setS({ ...s, date_suffix_baseline_shift_pct: parseFloat(e.target.value) })} className="bg-slate-900 border-slate-600 text-slate-100" /></div>
        <div className="flex items-center gap-2"><Switch checked={!!s.auto_centre_default} onCheckedChange={v => setS({ ...s, auto_centre_default: v })} /><Label className="text-slate-200">Auto-centre text by default</Label></div>
        <div className="flex items-center gap-2"><Switch checked={!!s.auto_fit_default} onCheckedChange={v => setS({ ...s, auto_fit_default: v })} /><Label className="text-slate-200">Auto-fit enabled by default</Label></div>
        <div className="md:col-span-2 flex justify-end pt-2">
          <Button onClick={save} disabled={saving} className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="save-text-settings-btn">{saving ? 'Saving…' : 'Save settings'}</Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================ API KEYS ============================
function ApiKeys({ API, token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState({});
  const [name, setName] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/corel/admin/api-keys`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(r.data || []);
    } catch (e) { toast.error('Failed to load keys'); }
    finally { setLoading(false); }
  }, [API, token]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim()) { toast.error('Give the key a name'); return; }
    try {
      const r = await axios.post(`${API}/corel/admin/api-keys`, { name: name.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      setRevealed(prev => ({ ...prev, [r.data.id]: true }));
      setName('');
      toast.success('Key created — copy it now');
      load();
    } catch (e) { toast.error('Create failed'); }
  };

  const toggleActive = async (k) => {
    await axios.put(`${API}/corel/admin/api-keys/${k.id}`, { active: !k.active }, { headers: { Authorization: `Bearer ${token}` } });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm('Permanently revoke this key?')) return;
    await axios.delete(`${API}/corel/admin/api-keys/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    toast.success('Key revoked'); load();
  };
  const copy = (val) => { navigator.clipboard.writeText(val); toast.success('Copied'); };

  return (
    <Card className="bg-slate-800/60 border-slate-700 text-slate-100">
      <CardHeader>
        <CardTitle className="text-xl text-white">API Keys</CardTitle>
        <CardDescription className="text-slate-400">One key per designer/workstation. Used by the plugin to authenticate.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input value={name} onChange={e => setName(e.target.value)} placeholder='e.g. "Designer A laptop"' className="bg-slate-900 border-slate-600 text-slate-100 placeholder:text-slate-500" data-testid="new-key-name-input" />
          <Button onClick={create} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="generate-key-btn"><Plus className="w-4 h-4 mr-2" />Generate Key</Button>
        </div>
        {loading ? <div className="text-slate-400">Loading…</div> : items.length === 0 ? (
          <div className="text-slate-500 italic">No keys yet — generate one above.</div>
        ) : (
          <div className="space-y-2">
            {items.map(k => {
              const show = revealed[k.id];
              const masked = show ? k.key : `${(k.key || '').slice(0, 8)}••••••••••••••••••••`;
              return (
                <div key={k.id} className="bg-slate-900 border border-slate-700 rounded-lg p-3" data-testid={`api-key-row-${k.id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-white">{k.name}</span>
                        {k.active ? <Badge className="bg-emerald-700 text-white">Active</Badge> : <Badge className="bg-slate-600 text-white">Revoked</Badge>}
                      </div>
                      <div className="font-mono text-xs text-slate-400 mt-1 break-all">{masked}</div>
                      <div className="text-xs text-slate-500 mt-1">Last used: {k.last_used_at || 'never'} · Plugin: {k.plugin_version || '—'} · Corel: {k.corel_version || '—'}</div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="border-slate-600 text-slate-200 hover:bg-slate-700" onClick={() => setRevealed(prev => ({ ...prev, [k.id]: !show }))}>
                        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button size="sm" variant="outline" className="border-slate-600 text-slate-200 hover:bg-slate-700" onClick={() => copy(k.key)}><Copy className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline" className="border-slate-600 text-slate-200 hover:bg-slate-700" onClick={() => toggleActive(k)} title={k.active ? 'Revoke' : 'Reactivate'}><Power className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline" className="border-red-700 text-red-400 hover:bg-red-900/30" onClick={() => remove(k.id)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
